import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../../js/Components/Logo';
import '../../css/Header.css';
import IsLoggedIn from '../../js/Components/IsLoggedIn';
import Cookies from 'js-cookie';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faCalendar, faTags, faChevronLeft, faChevronRight, faCog, faHouse, faImages, faMoon, faPhone, faRightFromBracket, faShieldHalved, faSun, faTooth, faUserLock, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useSiteSettings } from '../siteSettings';

interface HeaderProps {
  variant: 'container' | 'admin';
  themeMode: 'light' | 'dark';
  onToggleThemeMode: () => void;
  isAdminSidebarCollapsed?: boolean;
  onToggleAdminSidebar?: () => void;
}

const Header: React.FC<HeaderProps> = ({ variant, themeMode, onToggleThemeMode, isAdminSidebarCollapsed = false, onToggleAdminSidebar }) => {
  const [isNavExpanded, setIsNavExpanded] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { pages } = useSiteSettings();

  const handleLogout = () => {
    Cookies.remove('userLoggedIn');
    setIsNavExpanded(false);
    navigate('/');
  };

  const closePublicMenu = () => setIsNavExpanded(false);

  const publicNavItems = pages.filter((page) => page.show_in_web === 1 || page.is_system === 1).filter((page) => ['home', 'about', 'gallery', 'prices', 'contact', 'account'].includes(String(page.slug))).map((page) => ({
    ...page,
    icon: page.slug === 'home' ? faHouse : page.slug === 'about' ? faTooth : page.slug === 'gallery' ? faImages : page.slug === 'prices' ? faTags : page.slug === 'contact' ? faPhone : faUserLock,
  }));

  return (
    <header className={`header-${variant}`}>
      {variant === 'container' ? (
        <div>
          <div className="content-wrap">
            <div className="header-top">
              <div className="header-left">
                <FontAwesomeIcon icon={faPhone} className="header-info-icon" />
                <strong>Previa Cita </strong><span className="telefono_cita">33 34 75 81 91</span>
                <div className="direccion_cita">Teodomiro Manzano 4365, Guadalajara, Jal.</div>
              </div>
              <div className="header-center"><Logo /></div>
              <div className="header-right">
                <div className="header-actions">
                  <div className="header-schedule-card">
                    <div className="horario_label">Horario:</div>
                    <div className="horario_consulta">Lun - Sab: <strong>10am - 7pm</strong> Dom: Cerrado</div>
                  </div>
                  <button type="button" className="header-theme-toggle" onClick={onToggleThemeMode} aria-label={themeMode === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'} aria-pressed={themeMode === 'dark'}>
                    <span className="header-theme-toggle-icon"><FontAwesomeIcon icon={themeMode === 'dark' ? faSun : faMoon} /></span>
                    <span className="header-theme-toggle-copy">
                      <strong>{themeMode === 'dark' ? 'Claro' : 'Oscuro'}</strong>
                      <small>{themeMode === 'dark' ? 'Modo claro' : 'Modo oscuro'}</small>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="hr-top clearfix hr-shadow public-nav-shell">
            <div className="content-wrap public-nav-wrap">
              <button type="button" className="menu-toggle" aria-label={isNavExpanded ? 'Cerrar menú' : 'Abrir menú'} aria-expanded={isNavExpanded} onClick={() => setIsNavExpanded(!isNavExpanded)}>
                <FontAwesomeIcon icon={isNavExpanded ? faXmark : faBars} /><span>{isNavExpanded ? 'Cerrar' : 'Menú'}</span>
              </button>

              <nav className="Navbar_container" aria-label="Navegación principal">
                <ul className={`Navbar_list ${isNavExpanded ? 'active' : ''}`}>
                  {publicNavItems.map((item) => (
                    <li key={item.slug} className={`Navbar_list_li ${location.pathname === item.route ? 'Navbar_current' : ''}`}>
                      <Link to={item.route} onClick={closePublicMenu}><FontAwesomeIcon icon={item.icon} /><span>{item.nav_label}</span></Link>
                    </li>
                  ))}
                  <IsLoggedIn cookieName="userLoggedIn">
                    {{ exist: (<li className={`Navbar_list_li ${location.pathname.startsWith('/AdminPanel') ? 'Navbar_current' : ''}`}><Link to="/AdminPanel" onClick={closePublicMenu}><FontAwesomeIcon icon={faShieldHalved} /><span>Área interna</span></Link></li>) }}
                  </IsLoggedIn>
                  <IsLoggedIn cookieName="userLoggedIn">
                    {{ exist: (<li className="Navbar_list_li"><button type="button" className="nav-link-button" onClick={handleLogout}><FontAwesomeIcon icon={faRightFromBracket} /><span>Salir</span></button></li>) }}
                  </IsLoggedIn>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      ) : (
        <div className="admin-sidebar-inner">
          <Link to="/" className="sidebar-brand d-flex align-items-center justify-content-center" title="Volver al sitio"><Logo /></Link>
          <div className="sidebar-heading">Panel</div>
          <nav aria-label="Navegación de administrador">
            <ul className="navbar-nav bg-gradient-primary sidebar sidebar-light accordion" id="accordionSidebar">
              <li className={`nav-item ${location.pathname === '/AdminPanel' ? 'active' : ''}`}><Link to="/AdminPanel" className="nav-link"><FontAwesomeIcon icon={faHouse} /><span>Inicio</span></Link></li>
              <li className={`nav-item ${location.pathname === '/AdminPanel/Agenda' ? 'active' : ''}`}><Link to="/AdminPanel/Agenda" className="nav-link"><FontAwesomeIcon icon={faCalendar} /><span>Agenda</span></Link></li>
              <li className={`nav-item ${location.pathname === '/AdminPanel/Pacientes' ? 'active' : ''}`}><Link to="/AdminPanel/Pacientes" className="nav-link"><FontAwesomeIcon icon={faCog} /><span>Pacientes</span></Link></li>
            </ul>
          </nav>
          <button type="button" className="admin-theme-button" onClick={onToggleThemeMode} aria-label={themeMode === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}>
            <FontAwesomeIcon icon={themeMode === 'dark' ? faSun : faMoon} /><span>{themeMode === 'dark' ? 'Modo claro' : 'Modo oscuro'}</span>
          </button>
          <button type="button" className="sidebar-toggle" aria-label={isAdminSidebarCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'} onClick={onToggleAdminSidebar}>
            <FontAwesomeIcon icon={isAdminSidebarCollapsed ? faChevronRight : faChevronLeft} />
          </button>
          <button type="button" className="admin-logout-button" onClick={handleLogout}><FontAwesomeIcon icon={faRightFromBracket} /><span>Cerrar sesión</span></button>
        </div>
      )}
    </header>
  );
};

export default Header;
